import React, { useMemo } from 'react'
import { LuPlus } from 'react-icons/lu'

import CustomPickerButton from '@/components/integration/nylas/scheduler/components/simpleDayPicker/CustomPickerButton'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import {
  getDayKeyInTimezone,
  getSlotCountForDate,
} from '@/components/integration/nylas/scheduler/helpers'
import useSchedulerDays from '@/components/integration/nylas/scheduler/hooks/useSchedulerDays'
import { Button } from '@/components/ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { Spinner } from '@/components/ui/spinner'
import availabilityService from '@/lib/services/availabilityService'

function getDisplayDateFromDayKey(dayKey: string): Date {
  const [yearPart, monthPart, dayPart] = dayKey.split('-')
  const year = Number.parseInt(yearPart ?? '', 10)
  const month = Number.parseInt(monthPart ?? '', 10)
  const day = Number.parseInt(dayPart ?? '', 10)

  if ([year, month, day].some(Number.isNaN)) {
    return new Date()
  }

  return new Date(Date.UTC(year, month - 1, day, 12))
}

const SimpleDayPicker: React.FC = () => {
  const { timezone, owningEntityType, owningEntityId, availabilitySource } =
    useSchedulerConfig()
  const {
    selectedDate,
    selectedDateRange,
    selectDate,
    switchToCalendar,
    isExtendedAvailabilityReady,
    availabilityFilter,
  } = useSchedulerSelection()
  const { schedulerDays: firstWeekDays, isLoading } = useSchedulerDays(
    owningEntityType,
    owningEntityId,
    selectedDateRange.start,
    selectedDateRange.end,
    timezone,
    availabilityFilter,
    { availabilitySource },
  )

  const extendedAvailabilityRange = useMemo(
    () => availabilityService.getExtendedAvailabilityDateRange(),
    [],
  )

  const { schedulerDays: secondWeekDays } = useSchedulerDays(
    owningEntityType,
    owningEntityId,
    extendedAvailabilityRange.start,
    extendedAvailabilityRange.end,
    timezone,
    availabilityFilter,
    { enabled: isExtendedAvailabilityReady, availabilitySource },
  )

  const schedulerDays = useMemo(
    () => [...(firstWeekDays ?? []), ...(secondWeekDays ?? [])],
    [firstWeekDays, secondWeekDays],
  )

  /** Today + the following 6 or 13 days, anchored at UTC noon for stable display. */
  const dates: Date[] = useMemo(() => {
    const today = getDisplayDateFromDayKey(
      getDayKeyInTimezone(new Date(), timezone),
    )
    const numberOfDays = isExtendedAvailabilityReady ? 14 : 7

    return Array.from({ length: numberOfDays }, (_, index) => {
      const nextDate = new Date(today)
      nextDate.setUTCDate(today.getUTCDate() + index)

      return nextDate
    })
  }, [timezone, isExtendedAvailabilityReady])

  const selectedDayKey = selectedDate
    ? getDayKeyInTimezone(selectedDate, timezone)
    : null

  // Part of the day buttons' keys, so they pop in again when switching to or
  // from in person, as the availability shown follows different rules
  const availabilityRulesKey =
    availabilityFilter.inPersonFormat === null ? 'remote' : 'in-person'

  return (
    <Carousel
      opts={{
        align: 'start',
        // Ensures that we scroll by the whole width each time
        slidesToScroll: 'auto',
      }}
      className="w-full"
    >
      <CarouselContent>
        {dates.map((date, index) => {
          const dateKey = getDayKeyInTimezone(date, timezone)

          return (
            <CarouselItem
              key={`${availabilityRulesKey}-${date.toISOString()}`}
              className="basis-23"
            >
              <CustomPickerButton
                animationIndex={index}
                isLoading={isLoading}
                date={date}
                availableSlots={getSlotCountForDate(
                  dateKey,
                  schedulerDays,
                  timezone,
                  isLoading,
                )}
                onClick={() => selectDate(date)}
                isSelected={selectedDayKey === dateKey}
              />
            </CarouselItem>
          )
        })}

        {!isLoading && (
          <CarouselItem className="grow-0 basis-25">
            {isExtendedAvailabilityReady ? (
              <Button
                variant="outline"
                className="pop-in flex h-full w-full flex-col rounded-2xl"
                onClick={switchToCalendar}
              >
                <LuPlus className="text-foreground-dark size-6" />
                <span className="text-xs text-wrap">More dates</span>
              </Button>
            ) : (
              <div
                className="pop-in bg-accent flex h-full w-full flex-col
                  items-center justify-center gap-1 rounded-2xl border px-2
                  py-3"
              >
                <Spinner className="text-foreground size-6" />
                <span className="text-xs text-center text-wrap">
                  Getting more days
                </span>
              </div>
            )}
          </CarouselItem>
        )}
      </CarouselContent>

      <CarouselPrevious
        className="pop-in left-0 disabled:pointer-events-none disabled:hidden"
        variant="outline-secondary"
        size="icon-lg"
      />

      <CarouselNext
        className="pop-in right-0 disabled:pointer-events-none disabled:hidden"
        variant="outline-secondary"
        size="icon-lg"
      />
    </Carousel>
  )
}

export default SimpleDayPicker
